import React, { useState, useEffect } from 'react';
import './AMCCMCTracker.css';
import * as XLSX from 'xlsx';

const AMCCMCTracker = ({ currentUser }) => {
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [equipmentNameFilter, setEquipmentNameFilter] = useState('');
  const [contractTypeFilter, setContractTypeFilter] = useState('all');
  const [contractStatusFilter, setContractStatusFilter] = useState('all');
  
  // Date range filter states
  const [dateRangeFilter, setDateRangeFilter] = useState({
    startDate: '',
    endDate: '',
    type: 'all' // all, start, end
  });
  
  // New contract form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContractForm, setNewContractForm] = useState({
    equipmentName: '',
    manufacturer: '',
    contractType: 'AMC', // AMC or CMC
    vendorName: '',
    vendorContact: '',
    vendorEmail: '',
    vendorAddress: '',
    poNumber: '',
    poDate: '',
    poAmount: '',
    woNumber: '',
    woDate: '',
    woAmount: '',
    lastContractStartDate: '',
    lastContractEndDate: '',
    lastContractRate: '',
    newContractRate: '',
    rateDifference: '',
    rateDifferencePercentage: '',
    contractStartDate: '',
    contractEndDate: '',
    contractPeriod: '12', // in months
    paymentTerms: '',
    scopeOfWork: '',
    responseTime: '24 hours',
    preventiveVisits: '',
    calibrationVisits: '',
    contractStatus: 'Active',
    remarks: ''
  });

  // Editing state
  const [editingContract, setEditingContract] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    loadContracts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [contracts, dateFilter, searchTerm, equipmentNameFilter, contractTypeFilter, contractStatusFilter, dateRangeFilter]);

  const loadContracts = () => {
    try {
      setLoading(true);
      setError(null);
      const savedContracts = localStorage.getItem('amc-cmc-contracts');
      if (savedContracts) {
        const parsedContracts = JSON.parse(savedContracts);
        const validContracts = parsedContracts.filter(contract => contract && typeof contract === 'object');
        setContracts(validContracts);
        setFilteredContracts(validContracts);
      } else {
        setContracts([]);
        setFilteredContracts([]);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading contracts:', err);
      setError('Failed to load contracts: ' + err.message);
      setLoading(false);
    }
  };

  const saveContracts = (newContracts) => {
    try {
      localStorage.setItem('amc-cmc-contracts', JSON.stringify(newContracts));
      setContracts(newContracts);
      return true;
    } catch (error) {
      console.error('Failed to save contracts:', error);
      setError('Failed to save contracts: ' + error.message);
      return false;
    }
  };

  const safeToString = (value) => {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return value.toString();
    }
    try {
      return JSON.stringify(value);
    } catch (e) {
      return '';
    }
  };

  const applyFilters = () => {
    try {
      setError(null);
      let filtered = [...contracts];
      
      // Apply equipment name filter
      if (equipmentNameFilter.trim() !== '') {
        const term = equipmentNameFilter.toLowerCase().trim();
        filtered = filtered.filter(contract => {
          if (!contract || typeof contract !== 'object') return false;
          const equipmentName = safeToString(contract.equipmentName).toLowerCase();
          return equipmentName.includes(term);
        });
      }
      
      // Apply general search filter
      if (searchTerm.trim() !== '') {
        const term = searchTerm.toLowerCase().trim();
        filtered = filtered.filter(contract => {
          if (!contract || typeof contract !== 'object') return false;
          
          const equipmentName = safeToString(contract.equipmentName).toLowerCase();
          const manufacturer = safeToString(contract.manufacturer).toLowerCase();
          const vendorName = safeToString(contract.vendorName).toLowerCase();
          const vendorContact = safeToString(contract.vendorContact).toLowerCase();
          const poNumber = safeToString(contract.poNumber).toLowerCase();
          const woNumber = safeToString(contract.woNumber).toLowerCase();
          const woDate = safeToString(contract.woDate).toLowerCase();
          const woAmount = safeToString(contract.woAmount).toLowerCase();
          
          return (
            equipmentName.includes(term) ||
            manufacturer.includes(term) ||
            vendorName.includes(term) ||
            vendorContact.includes(term) ||
            poNumber.includes(term) ||
            woNumber.includes(term) ||
            woDate.includes(term) ||
            woAmount.includes(term)
          );
        });
      }
      
      // Apply contract type filter
      if (contractTypeFilter !== 'all') {
        filtered = filtered.filter(contract => {
          if (!contract || typeof contract !== 'object') return false;
          return contract.contractType === contractTypeFilter;
        });
      }
      
      // Apply contract status filter
      if (contractStatusFilter !== 'all') {
        filtered = filtered.filter(contract => {
          if (!contract || typeof contract !== 'object') return false;
          return contract.contractStatus === contractStatusFilter;
        });
      }
      
      // Apply date filter
      if (dateFilter !== 'all') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        filtered = filtered.filter(contract => {
          if (!contract || typeof contract !== 'object') return false;
          
          const endDate = contract.contractEndDate ? new Date(contract.contractEndDate) : null;
          
          if (dateFilter === 'expiringSoon') {
            // Contracts expiring in next 30 days
            if (!endDate) return false;
            const thirtyDaysFromNow = new Date(today);
            thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
            return endDate >= today && endDate <= thirtyDaysFromNow;
          } else if (dateFilter === 'expired') {
            // Contracts already expired
            if (!endDate) return false;
            return endDate < today;
          } else if (dateFilter === 'active') {
            // Active contracts
            if (!endDate) return false;
            return endDate >= today;
          }
          
          return true;
        });
      }
      
      setFilteredContracts(filtered);
    } catch (err) {
      console.error('Error applying filters:', err);
      setError('Failed to apply filters: ' + err.message);
    }
  };

  const handleAddContract = () => {
    // Validate required fields
    if (!newContractForm.equipmentName || !newContractForm.contractType || !newContractForm.vendorName) {
      alert('Please fill in Equipment Name, Contract Type, and Vendor Name');
      return;
    }
    
    // Check if at least one set (PO or WO) is filled
    const hasPO = newContractForm.poNumber || newContractForm.poDate || newContractForm.poAmount;
    const hasWO = newContractForm.woNumber || newContractForm.woDate || newContractForm.woAmount;
    
    if (!hasPO && !hasWO) {
      alert('Please fill in either PO fields (PO Number, PO Date, PO Amount) OR WO fields (WO Number, WO Date, WO Amount)');
      return;
    }
    
    // Validate PO fields if any PO field is filled
    if (hasPO && (!newContractForm.poNumber || !newContractForm.poDate || !newContractForm.poAmount)) {
      alert('If filling PO details, all PO fields (PO Number, PO Date, and PO Amount) are required');
      return;
    }
    
    // Validate WO fields if any WO field is filled
    if (hasWO && (!newContractForm.woNumber || !newContractForm.woDate || !newContractForm.woAmount)) {
      alert('If filling WO details, all WO fields (WO Number, WO Date, and WO Amount) are required');
      return;
    }
    
    const newContract = {
      id: Date.now().toString(),
      ...newContractForm,
      createdAt: new Date().toISOString(),
      createdBy: currentUser?.name || 'Unknown'
    };
    
    const updatedContracts = [...contracts, newContract];
    if (saveContracts(updatedContracts)) {
      setNewContractForm({
        equipmentName: '',
        manufacturer: '',
        contractType: 'AMC',
        vendorName: '',
        vendorContact: '',
        vendorEmail: '',
        vendorAddress: '',
        poNumber: '',
        poDate: '',
        poAmount: '',
        woNumber: '',
        woDate: '',
        woAmount: '',
        lastContractStartDate: '',
        lastContractEndDate: '',
        lastContractRate: '',
        newContractRate: '',
        rateDifference: '',
        rateDifferencePercentage: '',
        contractStartDate: '',
        contractEndDate: '',
        contractPeriod: '12',
        paymentTerms: '',
        scopeOfWork: '',
        responseTime: '24 hours',
        preventiveVisits: '',
        calibrationVisits: '',
        contractStatus: 'Active',
        remarks: ''
      });
      setShowAddForm(false);
      alert('Contract added successfully!');
    }
  };

  const handleEditContract = (contract) => {
    setEditingContract(contract.id);
    setEditForm({ ...contract });
  };

  const handleSaveEdit = () => {
    const updatedContracts = contracts.map(c => 
      c.id === editingContract ? { ...editForm, updatedAt: new Date().toISOString(), updatedBy: currentUser?.name || 'Unknown' } : c
    );
    
    if (saveContracts(updatedContracts)) {
      setEditingContract(null);
      setEditForm({});
      alert('Contract updated successfully!');
    }
  };

  const handleDeleteContract = (contractId) => {
    if (window.confirm('Are you sure you want to delete this contract?')) {
      const updatedContracts = contracts.filter(c => c.id !== contractId);
      saveContracts(updatedContracts);
      alert('Contract deleted successfully!');
    }
  };

  const calculateRateDifference = (lastRate, newRate) => {
    const last = parseFloat(lastRate) || 0;
    const newR = parseFloat(newRate) || 0;
    
    if (last === 0) return { difference: 0, percentage: 0 };
    
    const difference = newR - last;
    const percentage = ((difference / last) * 100).toFixed(2);
    
    return { difference, percentage };
  };

  const handleRateChange = (field, value, isEditMode = false) => {
    if (isEditMode) {
      const updatedForm = { ...editForm, [field]: value };
      const { difference, percentage } = calculateRateDifference(
        updatedForm.lastContractRate,
        updatedForm.newContractRate
      );
      setEditForm({
        ...updatedForm,
        rateDifference: difference,
        rateDifferencePercentage: percentage
      });
    } else {
      const updatedForm = { ...newContractForm, [field]: value };
      const { difference, percentage } = calculateRateDifference(
        updatedForm.lastContractRate,
        updatedForm.newContractRate
      );
      setNewContractForm({
        ...updatedForm,
        rateDifference: difference,
        rateDifferencePercentage: percentage
      });
    }
  };

  const clearAllFilters = () => {
    setDateFilter('all');
    setSearchTerm('');
    setEquipmentNameFilter('');
    setContractTypeFilter('all');
    setContractStatusFilter('all');
    setDateRangeFilter({ startDate: '', endDate: '', type: 'all' });
  };

  const exportToExcel = () => {
    const dataToExport = filteredContracts.map(contract => ({
      'Equipment Name': contract.equipmentName,
      'Manufacturer': contract.manufacturer,
      'Contract Type': contract.contractType,
      'Vendor Name': contract.vendorName,
      'Vendor Contact': contract.vendorContact,
      'Vendor Email': contract.vendorEmail,
      'Vendor Address': contract.vendorAddress,
      'PO Number': contract.poNumber,
      'PO Date': contract.poDate,
      'PO Amount': contract.poAmount,
      'WO Number': contract.woNumber,
      'WO Date': contract.woDate,
      'WO Amount': contract.woAmount,
      'Last Contract Start Date': contract.lastContractStartDate,
      'Last Contract End Date': contract.lastContractEndDate,
      'Last Contract Rate': contract.lastContractRate,
      'New Contract Rate': contract.newContractRate,
      'Rate Difference': contract.rateDifference,
      'Rate Difference %': contract.rateDifferencePercentage,
      'Contract Start Date': contract.contractStartDate,
      'Contract End Date': contract.contractEndDate,
      'Contract Period (Months)': contract.contractPeriod,
      'Payment Terms': contract.paymentTerms,
      'Scope of Work': contract.scopeOfWork,
      'Response Time': contract.responseTime,
      'Preventive Visits': contract.preventiveVisits,
      'Calibration Visits': contract.calibrationVisits,
      'Contract Status': contract.contractStatus,
      'Remarks': contract.remarks,
      'Created By': contract.createdBy,
      'Created At': contract.createdAt
    }));
    
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'AMC-CMC Contracts');
    XLSX.writeFile(wb, `AMC_CMC_Contracts_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleImportExcel = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);

        if (jsonData.length === 0) {
          alert('The file is empty or has no data.');
          return;
        }

        // Map imported data to contract format
        const importedContracts = jsonData.map((row, index) => {
          const lastRate = parseFloat(row['Last Contract Rate'] || row['lastContractRate']) || 0;
          const newRate = parseFloat(row['New Contract Rate'] || row['newContractRate']) || 0;
          const { difference, percentage } = calculateRateDifference(lastRate, newRate);
          
          return {
            id: Date.now().toString() + index,
            equipmentName: row['Equipment Name'] || row['equipmentName'] || '',
            manufacturer: row['Manufacturer'] || row['manufacturer'] || '',
            contractType: row['Contract Type'] || row['contractType'] || 'AMC',
            vendorName: row['Vendor Name'] || row['vendorName'] || '',
            vendorContact: row['Vendor Contact'] || row['vendorContact'] || '',
            vendorEmail: row['Vendor Email'] || row['vendorEmail'] || '',
            vendorAddress: row['Vendor Address'] || row['vendorAddress'] || '',
            poNumber: row['PO Number'] || row['poNumber'] || '',
            poDate: row['PO Date'] || row['poDate'] || '',
            poAmount: row['PO Amount'] || row['poAmount'] || '',
            woNumber: row['WO Number'] || row['woNumber'] || '',
            woDate: row['WO Date'] || row['woDate'] || '',
            woAmount: row['WO Amount'] || row['woAmount'] || '',
            lastContractStartDate: row['Last Contract Start Date'] || row['lastContractStartDate'] || '',
            lastContractEndDate: row['Last Contract End Date'] || row['lastContractEndDate'] || '',
            lastContractRate: lastRate || '',
            newContractRate: newRate || '',
            rateDifference: difference,
            rateDifferencePercentage: percentage,
            contractStartDate: row['Contract Start Date'] || row['contractStartDate'] || '',
            contractEndDate: row['Contract End Date'] || row['contractEndDate'] || '',
            contractPeriod: row['Contract Period (Months)'] || row['contractPeriod'] || '12',
            paymentTerms: row['Payment Terms'] || row['paymentTerms'] || '',
            scopeOfWork: row['Scope of Work'] || row['scopeOfWork'] || '',
            responseTime: row['Response Time'] || row['responseTime'] || '24 hours',
            preventiveVisits: row['Preventive Visits'] || row['preventiveVisits'] || '',
            calibrationVisits: row['Calibration Visits'] || row['calibrationVisits'] || '',
            contractStatus: row['Contract Status'] || row['contractStatus'] || 'Active',
            remarks: row['Remarks'] || row['remarks'] || '',
            createdAt: new Date().toISOString(),
            createdBy: currentUser?.name || 'Imported'
          };
        });

        // Add imported contracts to existing ones
        const updatedContracts = [...contracts, ...importedContracts];
        if (saveContracts(updatedContracts)) {
          alert(`Successfully imported ${importedContracts.length} contracts!`);
          event.target.value = ''; // Reset file input
        }
      } catch (error) {
        console.error('Error importing file:', error);
        alert('Error importing file. Please make sure the file format is correct.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const downloadTemplate = () => {
    const templateData = [
      {
        'Equipment Name': 'X-Ray Machine',
        'Manufacturer': 'Siemens',
        'Contract Type': 'AMC',
        'Vendor Name': 'ABC Medical Services',
        'Vendor Contact': '9876543210',
        'Vendor Email': 'contact@abcmedical.com',
        'Vendor Address': '123 Medical Street, City',
        'PO Number': 'PO-2024-001',
        'PO Date': '2024-01-01',
        'PO Amount': '50000',
        'WO Number': 'WO-2024-001',
        'WO Date': '2024-01-15',
        'WO Amount': '45000',
        'Last Contract Start Date': '2023-01-01',
        'Last Contract End Date': '2023-12-31',
        'Last Contract Rate': '45000',
        'New Contract Rate': '50000',
        'Rate Difference': '5000',
        'Rate Difference %': '11.11',
        'Contract Start Date': '2024-01-01',
        'Contract End Date': '2024-12-31',
        'Contract Period (Months)': '12',
        'Payment Terms': 'Net 30 days',
        'Scope of Work': 'Annual maintenance and repairs',
        'Response Time': '24 hours',
        'Preventive Visits': '2',
        'Calibration Visits': '1',
        'Contract Status': 'Active',
        'Remarks': 'Sample contract'
      }
    ];
    
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'AMC_CMC_Contract_Template.xlsx');
  };

  // Calculate dashboard statistics
  const dashboardStats = {
    total: contracts.length,
    amc: contracts.filter(c => c.contractType === 'AMC').length,
    cmc: contracts.filter(c => c.contractType === 'CMC').length,
    rental: contracts.filter(c => c.contractType === 'Rental').length,
    active: contracts.filter(c => {
      const endDate = c.contractEndDate ? new Date(c.contractEndDate) : null;
      const today = new Date();
      return endDate && endDate >= today;
    }).length,
    expiringSoon: contracts.filter(c => {
      const endDate = c.contractEndDate ? new Date(c.contractEndDate) : null;
      const today = new Date();
      if (!endDate) return false;
      const thirtyDaysFromNow = new Date(today);
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      return endDate >= today && endDate <= thirtyDaysFromNow;
    }).length,
    expired: contracts.filter(c => {
      const endDate = c.contractEndDate ? new Date(c.contractEndDate) : null;
      const today = new Date();
      return endDate && endDate < today;
    }).length
  };

  if (loading) {
    return <div className="loading">Loading AMC/CMC contracts...</div>;
  }

  return (
    <div className="amc-cmc-tracker-container">
      <div className="card">
        <h2 className="card-title">📋 AMC/CMC Contract Tracker</h2>
        
        {currentUser && (
          <div className="user-badge">
            Logged in as: <strong>{currentUser.name}</strong> ({currentUser.role})
          </div>
        )}
        
        {error && (
          <div className="error-message">{error}</div>
        )}
        
        {/* Dashboard Summary */}
        <div className="dashboard-summary">
          <h3>📊 Contract Summary</h3>
          <div className="summary-grid">
            <div className="summary-card total">
              <div className="summary-value">{dashboardStats.total}</div>
              <div className="summary-label">Total Contracts</div>
            </div>
            <div className="summary-card amc">
              <div className="summary-value">{dashboardStats.amc}</div>
              <div className="summary-label">AMC Contracts</div>
            </div>
            <div className="summary-card cmc">
              <div className="summary-value">{dashboardStats.cmc}</div>
              <div className="summary-label">CMC Contracts</div>
            </div>
            <div className="summary-card rental">
              <div className="summary-value">{dashboardStats.rental}</div>
              <div className="summary-label">Rental Contracts</div>
            </div>
            <div className="summary-card active">
              <div className="summary-value">{dashboardStats.active}</div>
              <div className="summary-label">Active Contracts</div>
            </div>
            <div className="summary-card expiring">
              <div className="summary-value">{dashboardStats.expiringSoon}</div>
              <div className="summary-label">Expiring Soon (30 days)</div>
            </div>
            <div className="summary-card expired">
              <div className="summary-value">{dashboardStats.expired}</div>
              <div className="summary-label">Expired Contracts</div>
            </div>
          </div>
        </div>
        
        {/* Filter Section */}
        <div className="filter-section">
          <h3>🔍 Search & Filters</h3>
          
          {/* Primary Search Row */}
          <div className="filter-row primary-search">
            <div className="filter-group search-large">
              <label htmlFor="equipmentSearch">🔧 Equipment Name:</label>
              <input
                type="text"
                id="equipmentSearch"
                className="search-input-large"
                placeholder="Search by equipment name... (e.g., X-Ray, ECG, Ventilator)"
                value={equipmentNameFilter}
                onChange={(e) => setEquipmentNameFilter(e.target.value)}
              />
              {equipmentNameFilter && (
                <button 
                  className="btn-clear-inline"
                  onClick={() => setEquipmentNameFilter('')}
                  title="Clear equipment search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          
          {/* Secondary Search Row */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="search">🔎 General Search:</label>
              <input
                type="text"
                id="search"
                placeholder="Search all fields (Asset ID, PO Number, Vendor, etc.)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="btn-clear-inline"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            
            <div className="filter-group">
              <label htmlFor="contractType">📄 Contract Type:</label>
              <select 
                id="contractType"
                value={contractTypeFilter}
                onChange={(e) => setContractTypeFilter(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="AMC">AMC</option>
                <option value="CMC">CMC</option>
                <option value="Rental">Rental</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="contractStatus">✅ Status:</label>
              <select 
                id="contractStatus"
                value={contractStatusFilter}
                onChange={(e) => setContractStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="Active">Active</option>
                <option value="Expired">Expired</option>
                <option value="Renewed">Renewed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="dateFilter">📅 Quick Filter:</label>
              <select 
                id="dateFilter"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="all">All Contracts</option>
                <option value="active">Active Only</option>
                <option value="expiringSoon">Expiring Soon (30 days)</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>&nbsp;</label>
              <button 
                className="btn btn-secondary"
                onClick={clearAllFilters}
                title="Clear all filters"
              >
                🔄 Clear All Filters
              </button>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="action-buttons">
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? '❌ Cancel' : '➕ Add New Contract'}
          </button>
          
          <label className="btn btn-info" style={{ margin: 0, cursor: 'pointer' }}>
            📤 Import from Excel
            <input
              type="file"
              accept=".xlsx, .xls, .csv"
              onChange={handleImportExcel}
              style={{ display: 'none' }}
            />
          </label>
          
          <button 
            className="btn btn-secondary"
            onClick={downloadTemplate}
          >
            📋 Download Template
          </button>
          
          <button 
            className="btn btn-success"
            onClick={exportToExcel}
            disabled={filteredContracts.length === 0}
          >
            📥 Export to Excel ({filteredContracts.length} contracts)
          </button>
        </div>
        
        {/* Add New Contract Form */}
        {showAddForm && (
          <div className="add-form-section">
            <h3>➕ Add New Contract</h3>
            <div className="form-grid">
              {/* Equipment Details Section */}
              <div className="form-group">
                <label>Equipment Name: *</label>
                <input
                  type="text"
                  value={newContractForm.equipmentName}
                  onChange={(e) => setNewContractForm({...newContractForm, equipmentName: e.target.value})}
                  placeholder="Enter equipment name"
                />
              </div>
              
              <div className="form-group">
                <label>Manufacturer: *</label>
                <input
                  type="text"
                  value={newContractForm.manufacturer}
                  onChange={(e) => setNewContractForm({...newContractForm, manufacturer: e.target.value})}
                  placeholder="Enter manufacturer"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Contract Type: *</label>
                <select
                  value={newContractForm.contractType}
                  onChange={(e) => setNewContractForm({...newContractForm, contractType: e.target.value})}
                >
                  <option value="AMC">AMC (Annual Maintenance Contract)</option>
                  <option value="CMC">CMC (Comprehensive Maintenance Contract)</option>
                  <option value="Rental">Rental</option>
                </select>
              </div>
              
              {/* Vendor Details Section */}
              <div className="form-group">
                <label>Vendor Name: *</label>
                <input
                  type="text"
                  value={newContractForm.vendorName}
                  onChange={(e) => setNewContractForm({...newContractForm, vendorName: e.target.value})}
                  placeholder="Enter vendor name"
                />
              </div>
              
              <div className="form-group">
                <label>Vendor Contact:</label>
                <input
                  type="text"
                  value={newContractForm.vendorContact}
                  onChange={(e) => setNewContractForm({...newContractForm, vendorContact: e.target.value})}
                  placeholder="Enter contact number"
                />
              </div>
              
              <div className="form-group">
                <label>Vendor Email:</label>
                <input
                  type="email"
                  value={newContractForm.vendorEmail}
                  onChange={(e) => setNewContractForm({...newContractForm, vendorEmail: e.target.value})}
                  placeholder="Enter vendor email"
                />
              </div>
              
              <div className="form-group full-width">
                <label>Vendor Address:</label>
                <textarea
                  value={newContractForm.vendorAddress}
                  onChange={(e) => setNewContractForm({...newContractForm, vendorAddress: e.target.value})}
                  placeholder="Enter vendor address"
                  rows="2"
                />
              </div>
              
              {/* PO Details Section */}
              <div className="form-group">
                <label>PO Number: *</label>
                <input
                  type="text"
                  value={newContractForm.poNumber}
                  onChange={(e) => setNewContractForm({...newContractForm, poNumber: e.target.value})}
                  placeholder="Enter PO number"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>PO Date:</label>
                <input
                  type="date"
                  value={newContractForm.poDate}
                  onChange={(e) => setNewContractForm({...newContractForm, poDate: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>PO Amount (₹):</label>
                <input
                  type="number"
                  value={newContractForm.poAmount}
                  onChange={(e) => setNewContractForm({...newContractForm, poAmount: e.target.value})}
                  placeholder="Enter amount"
                />
              </div>
              
              {/* WO Details Section */}
              <div className="form-group">
                <label>WO Number:</label>
                <input
                  type="text"
                  value={newContractForm.woNumber}
                  onChange={(e) => setNewContractForm({...newContractForm, woNumber: e.target.value})}
                  placeholder="Enter WO number"
                />
              </div>
              
              <div className="form-group">
                <label>WO Date:</label>
                <input
                  type="date"
                  value={newContractForm.woDate}
                  onChange={(e) => setNewContractForm({...newContractForm, woDate: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>WO Amount (₹):</label>
                <input
                  type="number"
                  value={newContractForm.woAmount}
                  onChange={(e) => setNewContractForm({...newContractForm, woAmount: e.target.value})}
                  placeholder="Enter amount"
                />
              </div>
              
              {/* Rate Comparison Section */}
              <div className="form-group">
                <label>Last Contract Start Date:</label>
                <input
                  type="date"
                  value={newContractForm.lastContractStartDate}
                  onChange={(e) => setNewContractForm({...newContractForm, lastContractStartDate: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Last Contract End Date:</label>
                <input
                  type="date"
                  value={newContractForm.lastContractEndDate}
                  onChange={(e) => setNewContractForm({...newContractForm, lastContractEndDate: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Last Contract Rate (₹):</label>
                <input
                  type="number"
                  value={newContractForm.lastContractRate}
                  onChange={(e) => handleRateChange('lastContractRate', e.target.value)}
                  placeholder="Enter last rate"
                />
              </div>
              
              <div className="form-group">
                <label>New Contract Rate (₹):</label>
                <input
                  type="number"
                  value={newContractForm.newContractRate}
                  onChange={(e) => handleRateChange('newContractRate', e.target.value)}
                  placeholder="Enter new rate"
                />
              </div>
              
              <div className="form-group">
                <label>Rate Difference (₹):</label>
                <input
                  type="text"
                  value={newContractForm.rateDifference}
                  readOnly
                  style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                  placeholder="Auto calculated"
                />
              </div>
              
              <div className="form-group">
                <label>Rate Difference (%):</label>
                <input
                  type="text"
                  value={newContractForm.rateDifferencePercentage ? `${newContractForm.rateDifferencePercentage}%` : ''}
                  readOnly
                  style={{ 
                    backgroundColor: '#f0f0f0', 
                    cursor: 'not-allowed',
                    color: parseFloat(newContractForm.rateDifferencePercentage) > 0 ? '#dc2626' : parseFloat(newContractForm.rateDifferencePercentage) < 0 ? '#16a34a' : '#000'
                  }}
                  placeholder="Auto calculated"
                />
              </div>
              
              {/* Contract Details Section */}
              <div className="form-group">
                <label>Contract Start Date:</label>
                <input
                  type="date"
                  value={newContractForm.contractStartDate}
                  onChange={(e) => setNewContractForm({...newContractForm, contractStartDate: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Contract End Date:</label>
                <input
                  type="date"
                  value={newContractForm.contractEndDate}
                  onChange={(e) => setNewContractForm({...newContractForm, contractEndDate: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>Contract Period (Months):</label>
                <input
                  type="number"
                  value={newContractForm.contractPeriod}
                  onChange={(e) => setNewContractForm({...newContractForm, contractPeriod: e.target.value})}
                  placeholder="12"
                />
              </div>
              
              <div className="form-group">
                <label>Payment Terms:</label>
                <input
                  type="text"
                  value={newContractForm.paymentTerms}
                  onChange={(e) => setNewContractForm({...newContractForm, paymentTerms: e.target.value})}
                  placeholder="e.g., Net 30 days"
                />
              </div>
              
              <div className="form-group">
                <label>Response Time:</label>
                <input
                  type="text"
                  value={newContractForm.responseTime}
                  onChange={(e) => setNewContractForm({...newContractForm, responseTime: e.target.value})}
                  placeholder="24 hours"
                />
              </div>
              
              <div className="form-group">
                <label>Preventive Visits (per year):</label>
                <input
                  type="number"
                  value={newContractForm.preventiveVisits}
                  onChange={(e) => setNewContractForm({...newContractForm, preventiveVisits: e.target.value})}
                  placeholder="2"
                />
              </div>
              
              <div className="form-group">
                <label>Calibration Visits (per year):</label>
                <input
                  type="number"
                  value={newContractForm.calibrationVisits}
                  onChange={(e) => setNewContractForm({...newContractForm, calibrationVisits: e.target.value})}
                  placeholder="1"
                />
              </div>
              
              <div className="form-group">
                <label>Contract Status:</label>
                <select
                  value={newContractForm.contractStatus}
                  onChange={(e) => setNewContractForm({...newContractForm, contractStatus: e.target.value})}
                >
                  <option value="Active">Active</option>
                  <option value="Expired">Expired</option>
                  <option value="Renewed">Renewed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              
              <div className="form-group full-width">
                <label>Scope of Work:</label>
                <textarea
                  value={newContractForm.scopeOfWork}
                  onChange={(e) => setNewContractForm({...newContractForm, scopeOfWork: e.target.value})}
                  placeholder="Describe scope of work"
                  rows="3"
                />
              </div>
              
              <div className="form-group full-width">
                <label>Remarks:</label>
                <textarea
                  value={newContractForm.remarks}
                  onChange={(e) => setNewContractForm({...newContractForm, remarks: e.target.value})}
                  placeholder="Additional notes"
                  rows="2"
                />
              </div>
            </div>
            
            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleAddContract}>
                ✅ Add Contract
              </button>
              <button className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                ❌ Cancel
              </button>
            </div>
          </div>
        )}
        
        {/* Contracts Table */}
        <div className="contracts-section">
          <h3>📋 Contracts List ({filteredContracts.length})</h3>
          
          {filteredContracts.length === 0 ? (
            <div className="no-data">
              <p>No contracts found. {searchTerm || equipmentNameFilter ? 'Try adjusting your filters.' : 'Add your first contract to get started!'}</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="contracts-table">
                <thead>
                  <tr>
                    <th>Equipment Name</th>
                    <th>Manufacturer</th>
                    <th>Type</th>
                    <th>Vendor</th>
                    <th>PO Number</th>
                    <th>PO Date</th>
                    <th>PO Amount</th>
                    <th>WO Number</th>
                    <th>WO Date</th>
                    <th>WO Amount</th>
                    <th>Last Start</th>
                    <th>Last End</th>
                    <th>Last Rate</th>
                    <th>New Rate</th>
                    <th>Difference</th>
                    <th>Diff %</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContracts.map(contract => (
                    <tr key={contract.id}>
                      {editingContract === contract.id ? (
                        <>
                          <td><input type="text" value={editForm.equipmentName} onChange={(e) => setEditForm({...editForm, equipmentName: e.target.value})} /></td>
                          <td><input type="text" value={editForm.manufacturer} onChange={(e) => setEditForm({...editForm, manufacturer: e.target.value})} /></td>
                          <td>
                            <select value={editForm.contractType} onChange={(e) => setEditForm({...editForm, contractType: e.target.value})}>
                              <option value="AMC">AMC</option>
                              <option value="CMC">CMC</option>
                              <option value="Rental">Rental</option>
                            </select>
                          </td>
                          <td><input type="text" value={editForm.vendorName} onChange={(e) => setEditForm({...editForm, vendorName: e.target.value})} /></td>
                          <td><input type="text" value={editForm.poNumber} onChange={(e) => setEditForm({...editForm, poNumber: e.target.value})} /></td>
                          <td><input type="date" value={editForm.poDate} onChange={(e) => setEditForm({...editForm, poDate: e.target.value})} /></td>
                          <td><input type="number" value={editForm.poAmount} onChange={(e) => setEditForm({...editForm, poAmount: e.target.value})} /></td>
                          <td><input type="text" value={editForm.woNumber} onChange={(e) => setEditForm({...editForm, woNumber: e.target.value})} /></td>
                          <td><input type="date" value={editForm.woDate} onChange={(e) => setEditForm({...editForm, woDate: e.target.value})} /></td>
                          <td><input type="number" value={editForm.woAmount} onChange={(e) => setEditForm({...editForm, woAmount: e.target.value})} /></td>
                          <td><input type="date" value={editForm.lastContractStartDate} onChange={(e) => setEditForm({...editForm, lastContractStartDate: e.target.value})} /></td>
                          <td><input type="date" value={editForm.lastContractEndDate} onChange={(e) => setEditForm({...editForm, lastContractEndDate: e.target.value})} /></td>
                          <td><input type="number" value={editForm.lastContractRate} onChange={(e) => handleRateChange('lastContractRate', e.target.value, true)} /></td>
                          <td><input type="number" value={editForm.newContractRate} onChange={(e) => handleRateChange('newContractRate', e.target.value, true)} /></td>
                          <td><input type="text" value={editForm.rateDifference} readOnly style={{backgroundColor: '#f0f0f0'}} /></td>
                          <td><input type="text" value={editForm.rateDifferencePercentage ? `${editForm.rateDifferencePercentage}%` : ''} readOnly style={{backgroundColor: '#f0f0f0'}} /></td>
                          <td><input type="date" value={editForm.contractStartDate} onChange={(e) => setEditForm({...editForm, contractStartDate: e.target.value})} /></td>
                          <td><input type="date" value={editForm.contractEndDate} onChange={(e) => setEditForm({...editForm, contractEndDate: e.target.value})} /></td>
                          <td>
                            <select value={editForm.contractStatus} onChange={(e) => setEditForm({...editForm, contractStatus: e.target.value})}>
                              <option value="Active">Active</option>
                              <option value="Expired">Expired</option>
                              <option value="Renewed">Renewed</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>
                          </td>
                          <td className="actions-cell">
                            <button className="btn-icon btn-save" onClick={handleSaveEdit} title="Save">💾</button>
                            <button className="btn-icon btn-cancel" onClick={() => setEditingContract(null)} title="Cancel">❌</button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{contract.equipmentName}</td>
                          <td>{contract.manufacturer || '-'}</td>
                          <td><span className={`badge badge-${contract.contractType.toLowerCase()}`}>{contract.contractType}</span></td>
                          <td>{contract.vendorName}</td>
                          <td>{contract.poNumber || '-'}</td>
                          <td>{contract.poDate || '-'}</td>
                          <td>{contract.poAmount ? `₹${parseFloat(contract.poAmount).toLocaleString()}` : '-'}</td>
                          <td>{contract.woNumber || '-'}</td>
                          <td>{contract.woDate || '-'}</td>
                          <td>{contract.woAmount ? `₹${parseFloat(contract.woAmount).toLocaleString()}` : '-'}</td>
                          <td>{contract.lastContractStartDate || '-'}</td>
                          <td>{contract.lastContractEndDate || '-'}</td>
                          <td>{contract.lastContractRate ? `₹${parseFloat(contract.lastContractRate).toLocaleString()}` : '-'}</td>
                          <td>{contract.newContractRate ? `₹${parseFloat(contract.newContractRate).toLocaleString()}` : '-'}</td>
                          <td style={{color: parseFloat(contract.rateDifference) > 0 ? '#dc2626' : parseFloat(contract.rateDifference) < 0 ? '#16a34a' : '#000'}}>
                            {contract.rateDifference ? `₹${parseFloat(contract.rateDifference).toLocaleString()}` : '-'}
                          </td>
                          <td style={{color: parseFloat(contract.rateDifferencePercentage) > 0 ? '#dc2626' : parseFloat(contract.rateDifferencePercentage) < 0 ? '#16a34a' : '#000', fontWeight: '600'}}>
                            {contract.rateDifferencePercentage ? `${contract.rateDifferencePercentage}%` : '-'}
                          </td>
                          <td>{contract.contractStartDate || '-'}</td>
                          <td>{contract.contractEndDate || '-'}</td>
                          <td><span className={`badge badge-status-${contract.contractStatus.toLowerCase()}`}>{contract.contractStatus}</span></td>
                          <td className="actions-cell">
                            <button className="btn-icon btn-edit" onClick={() => handleEditContract(contract)} title="Edit">✏️</button>
                            <button className="btn-icon btn-delete" onClick={(e) => { e.stopPropagation(); handleDeleteContract(contract.id); }} title="Delete">🗑️</button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AMCCMCTracker;
